import { create } from 'zustand';

export interface NavigationContext {
    jobId: string;
    stageId: string;
    internalStatus?: string;
    searchTerm?: string;
    sortField?: string;
    sortDirection?: string;
    filters?: Record<string, unknown>;
    sourceLabel: string;
    currentIndex: number;
    total: number;
}

export interface Breadcrumb {
    label: string;
    href?: string;
}

export type FullPagerEntityType = 'application' | 'person';

interface OpenParams {
    entityType: FullPagerEntityType;
    entityId: string;
    section?: string;
    navigationContext?: NavigationContext;
    breadcrumbs?: Breadcrumb[];
    taskId?: string;
    scope?: { reportKinds: string[] };
}

export interface FullPagerState {
    isOpen: boolean;
    entityType: FullPagerEntityType | null;
    entityId: string | null;
    activeSection: string | null;
    /** Optional task context — when present, the report renders with a "Mark complete" footer */
    taskId: string | null;
    /** Optional scope clip — restricts visible report kinds */
    scope: { reportKinds: string[] } | null;
    navigationContext: NavigationContext | null;
    breadcrumbs: Breadcrumb[];
    tocCollapsed: boolean;
    isNavigating: boolean;

    open: (params: OpenParams) => void;
    close: () => void;
    /**
     * Reconcile the store with whatever fp params are currently in `window.location`.
     * Called by the shell on active-tab change so the overlay follows the active tab's URL.
     * Idempotent: reads URL + sessionStorage and updates state without writing back to either.
     */
    syncFromUrl: () => void;
    navigateTo: (index: number) => Promise<void>;
    setSection: (section: string) => void;
    toggleToc: () => void;
    setEntityId: (id: string) => void;
    setBreadcrumbs: (breadcrumbs: Breadcrumb[]) => void;
    updateNavigation: (ctx: Partial<NavigationContext>) => void;
    setTaskContext: (taskId: string | null, scope?: { reportKinds: string[] } | null) => void;
}

// ── URL param sync ──────────────────────────────────────────────────────────

function pushUrlParams(state: Pick<FullPagerState, 'entityType' | 'entityId' | 'activeSection' | 'taskId' | 'scope'>) {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (state.entityType && state.entityId) {
        url.searchParams.set('fp', `${state.entityType}:${state.entityId}`);
        if (state.activeSection) {
            url.searchParams.set('s', state.activeSection);
        } else {
            url.searchParams.delete('s');
        }
        if (state.taskId) url.searchParams.set('task', state.taskId);
        else url.searchParams.delete('task');
        if (state.scope?.reportKinds?.length) {
            url.searchParams.set('scope', state.scope.reportKinds.join(','));
        } else {
            url.searchParams.delete('scope');
        }
    } else {
        url.searchParams.delete('fp');
        url.searchParams.delete('s');
        url.searchParams.delete('task');
        url.searchParams.delete('scope');
    }
    window.history.replaceState(window.history.state, '', url.toString());
}

function clearUrlParams() {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    url.searchParams.delete('fp');
    url.searchParams.delete('s');
    url.searchParams.delete('task');
    url.searchParams.delete('scope');
    window.history.replaceState(window.history.state, '', url.toString());
}

export function parseUrlParams(): {
    entityType: FullPagerEntityType;
    entityId: string;
    section: string | null;
    taskId: string | null;
    scope: { reportKinds: string[] } | null;
} | null {
    if (typeof window === 'undefined') return null;
    const url = new URL(window.location.href);
    const fp = url.searchParams.get('fp');
    if (!fp) return null;
    const [entityType, ...rest] = fp.split(':');
    const entityId = rest.join(':');
    if (!entityType || !entityId) return null;
    if (entityType !== 'application' && entityType !== 'person') return null;
    const section = url.searchParams.get('s');
    const taskId = url.searchParams.get('task');
    const scopeRaw = url.searchParams.get('scope');
    const scope = scopeRaw
        ? { reportKinds: scopeRaw.split(',').map((s) => s.trim()).filter(Boolean) }
        : null;
    return { entityType: entityType as FullPagerEntityType, entityId, section, taskId, scope };
}

// ── sessionStorage persistence for NavigationContext ─────────────────────────

const NAV_CONTEXT_KEY = 'fullpager-nav-context';
const BREADCRUMBS_KEY = 'fullpager-breadcrumbs';

function persistContext(navCtx: NavigationContext | null, breadcrumbs: Breadcrumb[]) {
    if (typeof window === 'undefined') return;
    try {
        if (navCtx) {
            sessionStorage.setItem(NAV_CONTEXT_KEY, JSON.stringify(navCtx));
        } else {
            sessionStorage.removeItem(NAV_CONTEXT_KEY);
        }
        if (breadcrumbs.length > 0) {
            sessionStorage.setItem(BREADCRUMBS_KEY, JSON.stringify(breadcrumbs));
        } else {
            sessionStorage.removeItem(BREADCRUMBS_KEY);
        }
    } catch { /* quota or private browsing */ }
}

function clearPersistedContext() {
    if (typeof window === 'undefined') return;
    try {
        sessionStorage.removeItem(NAV_CONTEXT_KEY);
        sessionStorage.removeItem(BREADCRUMBS_KEY);
    } catch { /* ignore */ }
}

export function restorePersistedState(): { navigationContext: NavigationContext | null; breadcrumbs: Breadcrumb[] } {
    if (typeof window === 'undefined') return { navigationContext: null, breadcrumbs: [] };
    try {
        const navRaw = sessionStorage.getItem(NAV_CONTEXT_KEY);
        const bcRaw = sessionStorage.getItem(BREADCRUMBS_KEY);
        return {
            navigationContext: navRaw ? JSON.parse(navRaw) : null,
            breadcrumbs: bcRaw ? JSON.parse(bcRaw) : [],
        };
    } catch {
        return { navigationContext: null, breadcrumbs: [] };
    }
}

// ── Store ───────────────────────────────────────────────────────────────────

const TOC_STORAGE_KEY = 'fullpager-toc-collapsed';

export const useFullPagerStore = create<FullPagerState>((set, get) => ({
    isOpen: false,
    entityType: null,
    entityId: null,
    activeSection: null,
    taskId: null,
    scope: null,
    navigationContext: null,
    breadcrumbs: [],
    tocCollapsed: typeof window !== 'undefined' && localStorage.getItem(TOC_STORAGE_KEY) === 'true',
    isNavigating: false,

    open: (params) => {
        const section = params.section || 'summary';
        const navCtx = params.navigationContext || null;
        const breadcrumbs = params.breadcrumbs || [];
        const taskId = params.taskId || null;
        const scope = params.scope || null;
        set({
            isOpen: true,
            entityType: params.entityType,
            entityId: params.entityId,
            activeSection: section,
            taskId,
            scope,
            navigationContext: navCtx,
            breadcrumbs,
            isNavigating: false,
        });
        pushUrlParams({
            entityType: params.entityType,
            entityId: params.entityId,
            activeSection: section,
            taskId,
            scope,
        });
        persistContext(navCtx, breadcrumbs);
    },

    close: () => {
        set({
            isOpen: false,
            entityType: null,
            entityId: null,
            activeSection: null,
            taskId: null,
            scope: null,
            navigationContext: null,
            breadcrumbs: [],
            isNavigating: false,
        });
        clearUrlParams();
        clearPersistedContext();
    },

    syncFromUrl: () => {
        const parsed = parseUrlParams();
        const current = get();

        if (!parsed) {
            // No fp params in active tab's URL — close if open, but DON'T touch
            // sessionStorage (other tabs may rely on it for nav-context restore).
            if (current.isOpen) {
                set({
                    isOpen: false,
                    entityType: null,
                    entityId: null,
                    activeSection: null,
                    taskId: null,
                    scope: null,
                    navigationContext: null,
                    breadcrumbs: [],
                    isNavigating: false,
                });
            }
            return;
        }

        // Active tab has fp params. If state already matches, no-op.
        const sectionFromUrl = parsed.section || 'summary';
        if (
            current.isOpen &&
            current.entityType === parsed.entityType &&
            current.entityId === parsed.entityId &&
            current.activeSection === sectionFromUrl &&
            current.taskId === parsed.taskId &&
            JSON.stringify(current.scope) === JSON.stringify(parsed.scope)
        ) {
            return;
        }

        // Restore navigation context if available — single shared sessionStorage
        // means cross-tab nav context isn't strictly per-tab, but it's better
        // than nothing for the common case (return to same tab you opened from).
        const persisted = restorePersistedState();

        set({
            isOpen: true,
            entityType: parsed.entityType,
            entityId: parsed.entityId,
            activeSection: sectionFromUrl,
            taskId: parsed.taskId,
            scope: parsed.scope,
            navigationContext: persisted.navigationContext,
            breadcrumbs: persisted.breadcrumbs,
            isNavigating: false,
        });
    },

    navigateTo: async (index) => {
        const { navigationContext, entityType, activeSection, breadcrumbs } = get();
        if (!navigationContext || get().isNavigating) return;

        set({ isNavigating: true });

        try {
            const { applicationApi } = await import('@/modules/application/services/application.api');
            const { data } = await applicationApi.navigateInContext({
                jobPostingId: navigationContext.jobId,
                stageId: navigationContext.stageId,
                internalStatus: navigationContext.internalStatus,
                targetIndex: index,
                searchTerm: navigationContext.searchTerm,
                sortField: navigationContext.sortField,
                sortDirection: navigationContext.sortDirection,
                filters: navigationContext.filters,
            });

            if (!data.applicationId) {
                set({ isNavigating: false });
                return;
            }

            const updatedCtx: NavigationContext = {
                ...navigationContext,
                currentIndex: data.indexInContext,
                total: data.totalInContext,
            };

            const updatedBreadcrumbs = data.candidateName
                ? [...breadcrumbs.slice(0, -1), { label: data.candidateName }]
                : breadcrumbs;

            set({
                entityId: data.applicationId,
                navigationContext: updatedCtx,
                breadcrumbs: updatedBreadcrumbs,
                isNavigating: false,
            });

            pushUrlParams({
                entityType,
                entityId: data.applicationId,
                activeSection,
                taskId: get().taskId,
                scope: get().scope,
            });
            persistContext(updatedCtx, updatedBreadcrumbs);
        } catch (err) {
            console.error('[fullpager] navigate error:', err);
            set({ isNavigating: false });
        }
    },

    setSection: (section) => {
        set({ activeSection: section });
        const { entityType, entityId, taskId, scope } = get();
        pushUrlParams({ entityType, entityId, activeSection: section, taskId, scope });
    },

    toggleToc: () => {
        const next = !get().tocCollapsed;
        set({ tocCollapsed: next });
        if (typeof window !== 'undefined') {
            localStorage.setItem(TOC_STORAGE_KEY, String(next));
        }
    },

    setEntityId: (id) => {
        set({ entityId: id });
        const { entityType, activeSection, taskId, scope } = get();
        pushUrlParams({ entityType, entityId: id, activeSection, taskId, scope });
    },

    setTaskContext: (taskId, scope = null) => {
        set({ taskId, scope });
        const { entityType, entityId, activeSection } = get();
        pushUrlParams({ entityType, entityId, activeSection, taskId, scope });
    },

    setBreadcrumbs: (breadcrumbs) => set({ breadcrumbs }),

    updateNavigation: (partial) => {
        const current = get().navigationContext;
        if (!current) return;
        const updated = { ...current, ...partial };
        set({ navigationContext: updated });
        persistContext(updated, get().breadcrumbs);
    },
}));
